/**
 * InfinitSales - RecordingBar
 *
 * Conteudo interno do input do chat enquanto o usuario grava um audio
 * (estilo WhatsApp): no lugar do anexo aparece um botao de cancelar (lixeira),
 * no meio uma animacao de "gravando" com o tempo decorrido, e no lugar do
 * enviar aparece o botao que para a gravacao e envia como audio.
 *
 * Renderiza apenas o conteudo da linha — o container arredondado (inputWrapper)
 * continua sendo do ChatWindow para manter a identidade visual.
 */
import React from 'react';
import { Trash2, Send, Loader2 } from 'lucide-react';

const formatElapsed = (totalSeconds) => {
  const s = Math.max(0, Math.floor(totalSeconds || 0));
  const mm = String(Math.floor(s / 60)).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');
  return `${mm}:${ss}`;
};

const RecordingBar = ({ seconds, sending, onCancel, onSend, colors }) => {
  const danger = colors.danger || '#ef4444';

  const btnBase = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '34px',
    height: '34px',
    minWidth: '34px',
    border: 'none',
    borderRadius: '50%',
    cursor: 'pointer',
    flexShrink: 0,
    transition: 'opacity 0.15s ease',
  };

  return (
    <>
      {/* Cancelar — descarta o audio (substitui o anexo) */}
      <button
        type="button"
        onClick={onCancel}
        disabled={sending}
        title="Cancelar gravacao"
        style={{
          ...btnBase,
          background: 'transparent',
          color: danger,
          ...(sending ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
        }}
      >
        <Trash2 size={18} />
      </button>

      {/* Indicador de gravacao + tempo */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '6px 4px',
          minWidth: 0,
        }}
      >
        <span
          style={{
            width: '9px',
            height: '9px',
            borderRadius: '50%',
            backgroundColor: danger,
            flexShrink: 0,
            animation: 'recPulse 1.2s ease-in-out infinite',
          }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: '3px', flex: 1, minWidth: 0 }}>
          {[0, 1, 2, 3, 4].map((i) => (
            <span
              key={i}
              style={{
                width: '3px',
                borderRadius: '2px',
                backgroundColor: colors.textSecondary,
                animation: 'recWave 1s ease-in-out infinite',
                animationDelay: `${i * 0.12}s`,
              }}
            />
          ))}
        </div>
        <span
          style={{
            fontSize: '13px',
            fontVariantNumeric: 'tabular-nums',
            color: colors.textSecondary,
            flexShrink: 0,
          }}
        >
          {formatElapsed(seconds)}
        </span>
      </div>

      {/* Parar e enviar como audio (substitui o enviar) */}
      <button
        type="button"
        onClick={onSend}
        disabled={sending}
        title="Enviar audio"
        style={{
          ...btnBase,
          backgroundColor: colors.accentPrimary,
          color: '#fff',
          ...(sending ? { opacity: 0.6, cursor: 'not-allowed' } : {}),
        }}
      >
        {sending ? (
          <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
        ) : (
          <Send size={16} />
        )}
      </button>

      <style>{`
        @keyframes recPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.35; transform: scale(0.82); }
        }
        @keyframes recWave {
          0%, 100% { height: 6px; }
          50% { height: 18px; }
        }
      `}</style>
    </>
  );
};

export default RecordingBar;
